import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { useAppBanner } from '@/contexts/appBanner';

/**
 * Content for the row directly above the banner — the natural home for a
 * screen's options, menu or title. Right-aligned by the row itself.
 *
 * Renders nothing until the banner has mounted its target node (one extra
 * render on first paint), which is why routes may declare it unconditionally.
 */
export function BannerTopSlot({ children }: { children: ReactNode }) {
  const { topNode } = useAppBanner();
  if (!topNode) return null;
  return createPortal(children, topNode);
}

/**
 * La colonna a destra del banner compresso.
 *
 * Rende `null` quando il banner e' esteso: quella colonna non esiste, e il suo
 * contenuto (il tasto della sezione attiva) non deve comparire da nessun'altra
 * parte — a banner esteso la griglia e' visibile e lo direbbe due volte.
 */
export function BannerSideSlot({ children }: { children: ReactNode }) {
  const { sideNode } = useAppBanner();
  if (!sideNode) return null;
  return createPortal(children, sideNode);
}

/**
 * La riga SOTTO il banner, ancora dentro la chrome.
 *
 * È il posto dell'identità: sul Profilo ci vanno nickname e barra
 * dell'esperienza. Sta qui e non nella rotta perché deve finire dentro la targa
 * di cuoio che fa da fondo al blocco — dalla rotta sarebbe uscita sopra le
 * scaglie, e la targa avrebbe tagliato l'identità a metà.
 */
export function BannerBottomSlot({ children }: { children: ReactNode }) {
  const { bottomNode } = useAppBanner();
  if (!bottomNode) return null;
  return createPortal(children, bottomNode);
}

/**
 * Content painted OVER the banner, covering exactly its box — absolutely
 * positioned children anchor to the banner's edges.
 *
 * The layer is `pointer-events-none` so taps fall through to the banner
 * itself (which opens the customize sheet). Anything tappable inside must
 * opt back in with `pointer-events-auto` and bound its own footprint.
 */
export function BannerOverlaySlot({ children }: { children: ReactNode }) {
  const { overlayNode } = useAppBanner();
  if (!overlayNode) return null;
  return createPortal(children, overlayNode);
}
